import { createRoot, hydrateRoot } from 'react-dom/client';
import { StrictMode } from 'react';
import App from './App.tsx';
import './index.css';
import './i18n/config';
import { setupGlobalErrorHandlers } from './utils/errorHandlers';
import { initGlobalPrefetch } from './utils/prefetchManager';
import { initAmplitudeIfConsented } from './services/analytics';
import { initPostHog } from './services/posthog';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Sentry + PostHog — chargés après rendu initial pour ne pas bloquer TTI
const initMonitoring = () => {
  // Sentry — prod uniquement
  if (SENTRY_DSN && import.meta.env.PROD) {
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: SENTRY_DSN,
        enabled: import.meta.env.PROD,
        environment: import.meta.env.MODE,
        release: 'octoveto@' + (import.meta.env.VITE_APP_VERSION || 'dev'),
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event) {
          const msg = event.exception?.values?.[0]?.value ?? '';
          const url = event.request?.url ?? '';
          if (url.startsWith('chrome-extension://') || url.startsWith('moz-extension://')) return null;
          const ignoredErrors = [
            'ResizeObserver loop limit exceeded',
            'Non-Error promise rejection',
            'Network request failed',
            'NetworkError',
            'Load failed',
            'Failed to fetch',
            'Amplitude',
            'Script error',
          ];
          if (ignoredErrors.some(m => msg.includes(m))) return null;
          return event;
        },
        denyUrls: [
          /extensions\//i,
          /chrome-extension:\/\//i,
          /^chrome:\/\//i,
        ],
      });
    });
  }

  // PostHog
  initPostHog();
};

if ('requestIdleCallback' in window) {
  requestIdleCallback(initMonitoring, { timeout: 3000 });
} else {
  setTimeout(initMonitoring, 1000);
}

setupGlobalErrorHandlers();

try {
  initAmplitudeIfConsented();
} catch {
  // non critique
}

initGlobalPrefetch();

const rootEl = document.getElementById('root')!;
const isPrerendered = document.body.dataset.prerendered === 'true';
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

// Pages prerendues : hydrateRoot conserve les éléments DOM existants → LCP = paint initial (rapide)
// createRoot pour les autres pages (splash + full render)
if (isPrerendered) {
  hydrateRoot(rootEl, app, {
    onRecoverableError: () => { /* silencieux — mismatches mineurs acceptés */ }
  });
} else {
  createRoot(rootEl).render(app);
}

if (!isPrerendered) {
  // Splash uniquement pour les pages non-prerendues
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const splash = document.getElementById('__octoveto_splash');
      if (splash) {
        splash.style.opacity = '0';
        splash.style.transition = 'opacity 0.15s ease';
        setTimeout(() => splash.remove(), 150);
      }
    });
  });
}
